import React from 'react';
import { Sparkles, ShieldCheck } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * Renders an AI-generated ad in a polished mock-feed card. Used on the
 * landing page "try before you sign up" preview and inside the post-signup
 * onboarding flow.
 */
const AdPreviewCard = ({ ad, businessName, blur = false }) => {
  if (!ad) return null;
  const { headline, body, cta, hashtags } = ad;
  return (
    <div
      data-testid="ad-preview-card"
      className={`relative bg-white rounded-2xl shadow-xl shadow-rose-200/40 border border-slate-200 overflow-hidden ${
        blur ? 'select-none pointer-events-none' : ''
      }`}
    >
      {/* Mock Meta-style header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-100">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-white font-bold">
          {(businessName || 'B').slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{businessName || 'Your business'}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1">
            Sponsored · <ShieldCheck className="w-3 h-3 text-emerald-500" />
            <span>ASA-friendly</span>
          </div>
        </div>
        <Badge className="bg-orange-50 text-orange-700 border-orange-200 font-medium">
          <Sparkles className="w-3 h-3 mr-1" /> AI Draft
        </Badge>
      </div>

      <div className={`p-5 ${blur ? 'filter blur-md' : ''}`}>
        <div className="font-extrabold text-xl text-slate-900 leading-snug">{headline}</div>
        <p className="mt-3 text-slate-700 whitespace-pre-wrap leading-relaxed text-[15px]">{body}</p>
        <div className="mt-4 text-sm text-orange-600 font-medium break-words">{hashtags}</div>
      </div>

      {/* Visual placeholder for the image slot (real image takes ~30s; we
          intentionally do NOT auto-fire image gen on the public preview to
          keep latency snappy and the Gemini Nano Banana bill predictable) */}
      <div className={`mx-5 mb-5 aspect-[4/5] rounded-xl bg-gradient-to-br from-amber-100 via-rose-100 to-orange-100 border border-slate-100 flex items-center justify-center ${blur ? 'filter blur-md' : ''}`}>
        <div className="text-center text-slate-500 px-6">
          <Sparkles className="w-7 h-7 text-orange-400 mx-auto" />
          <div className="mt-2 text-sm font-semibold text-slate-700">AI image included in trial</div>
          <div className="text-xs mt-1">Photoreal 1:1 ad images generated alongside your copy.</div>
        </div>
      </div>

      <div className="px-5 pb-5">
        <div className={`w-full h-11 rounded-xl bg-slate-900 text-white font-semibold flex items-center justify-center ${blur ? 'filter blur-md' : ''}`}>
          {cta || 'Learn more'}
        </div>
      </div>
    </div>
  );
};

export default AdPreviewCard;
